import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { bookingsAPI } from '../services/api';
import { Booking } from '../types';
import { MapPin, User, Phone, Mail, Star, CheckCircle, Clock, Navigation, Shield, AlertCircle } from 'lucide-react';

const CustomerMapView: React.FC = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();
  
  const [booking, setBooking] = useState<Booking | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (bookingId) {
      fetchBooking();
      // Poll for updates every 5 seconds
      const interval = setInterval(fetchBooking, 5000);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  const fetchBooking = async () => {
    try {
      setLoading(true);
      const response = await bookingsAPI.getBookingById(bookingId!);
      const bookingData = response.data.booking;
      
      // Debug logging for OTP
      console.log('Customer Trip View - Booking Data:', {
        bookingId: bookingData._id || bookingData.id,
        tripOtp: bookingData.tripOtp,
        otpVerified: bookingData.otpVerified,
        status: bookingData.status,
        serviceType: bookingData.serviceType
      });
      
      setBooking(bookingData);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load booking');
    } finally {
      setLoading(false);
    }
  };

  const openDriverLocation = () => {
    if (booking?.driverCurrentLocation) {
      const { latitude, longitude } = booking.driverCurrentLocation;
      const url = `https://www.google.com/maps?q=${latitude},${longitude}`;
      window.open(url, '_blank');
    }
  };

  const openPickupInMaps = () => {
    if (booking?.pickupLocation) {
      const { latitude, longitude } = booking.pickupLocation;
      const url = `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`;
      window.open(url, '_blank');
    }
  };

  if (loading && !booking) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4"></div>
          <p className="text-gray-300">Loading trip details...</p>
        </div>
      </div>
    );
  }

  if (error && !booking) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Error</h2>
          <p className="text-gray-600 mb-4">{error || 'Booking not found'}</p>
          <button
            onClick={() => navigate('/bookings')}
            className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800"
          >
            Back to My Bookings
          </button>
        </div>
      </div>
    );
  }

  const driver = booking?.provider || (typeof booking?.providerId === 'object' ? booking.providerId : null);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Page Title */}
        <div className="bg-gradient-to-r from-black to-gray-600 text-white rounded-lg px-6 py-6 shadow-lg">
          <h1 className="text-2xl font-bold mb-2">Your Trip</h1>
          <p className="text-white mb-2">
            {booking?.otpVerified ? 'Trip in Progress' : 'Waiting for Driver'}
          </p>
          <p className="text-gray-200 text-sm">
            Booking ID: {(booking?.bookingId || booking?._id || booking?.id)?.toString().slice(-8).toUpperCase()}
          </p>
        </div>
        {/* Trip Started Confirmation */}
        {booking?.otpVerified && (
          <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6">
            <div className="flex items-center justify-center mb-4">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-green-900 text-center mb-2">Trip Started!</h3>
            <p className="text-green-700 text-center">
              Your driver has verified the OTP and started the trip.
            </p>
            {booking.tripActualStartDateTime && (
              <p className="text-sm text-green-600 text-center mt-2">
                Started at: {new Date(booking.tripActualStartDateTime).toLocaleTimeString()}
              </p>
            )}
          </div>
        )}

        {/* Driver Details */}
        {driver && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
              <User className="h-5 w-5 mr-2 text-black" />
              Your Driver
            </h2>
            <div className="flex items-center space-x-4 mb-4">
              {driver.avatar ? (
                <img
                  src={driver.avatar}
                  alt={driver.name}
                  className="h-20 w-20 rounded-full object-cover border-4 border-black100"
                />
              ) : (
                <div className="h-20 w-20 rounded-full bg-gray-100 flex items-center justify-center border-4 border-gray-300">
                  <User className="h-10 w-10 text-black" />
                </div>
              )}
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900">{driver.name}</h3>
                {driver.rating && (
                  <div className="flex items-center mt-1">
                    <Star className="h-4 w-4 text-yellow-400 fill-current" />
                    <span className="ml-1 text-sm font-semibold text-gray-700">
                      {driver.rating.toFixed(1)}
                    </span>
                    <span className="ml-1 text-sm text-gray-500">
                      ({driver.reviewCount || 0} reviews)
                    </span>
                  </div>
                )}
                {driver.isVerified && (
                  <div className="inline-flex items-center px-2 py-1 rounded-full bg-green-100 text-green-800 text-xs font-medium mt-2">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Verified Driver
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-2">
              {driver.phone && (
                <a
                  href={`tel:${driver.phone}`}
                  className="flex items-center text-black hover:text-gray-800 font-medium"
                >
                  <Phone className="h-4 w-4 mr-2" />
                  {driver.phone}
                </a>
              )}
              {driver.email && (
                <a
                  href={`mailto:${driver.email}`}
                  className="flex items-center text-black hover:text-gray-800 font-medium"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  {driver.email}
                </a>
              )}
            </div>
          </div>
        )}

        {/* Driver Location (if trip started) */}
        {booking?.otpVerified && booking.driverCurrentLocation && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
              <Navigation className="h-5 w-5 mr-2 text-black" />
              Driver Location
            </h2>
            <div className="bg-blue-50 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-blue-900">Real-time Tracking Active</span>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse mr-2"></div>
                  <span className="text-xs text-blue-600">Live</span>
                </div>
              </div>
              <p className="text-xs text-blue-700">
                Last updated: {new Date(booking.driverCurrentLocation.lastUpdated).toLocaleTimeString()}
              </p>
            </div>
            <button
              onClick={openDriverLocation}
              className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center font-medium shadow-sm"
            >
              <MapPin className="h-5 w-5 mr-2" />
              View Driver Location on Map
            </button>
          </div>
        )}

        {/* Trip Route */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
            <MapPin className="h-5 w-5 mr-2 text-black" />
            Trip Route
          </h2>
          <div className="space-y-4">
            {/* Pickup Location */}
            <div className="flex items-start">
              <div className="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mr-4">
                <MapPin className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-green-700 mb-1">PICKUP</p>
                <p className="text-gray-900">{booking?.pickupLocation?.address}</p>
              </div>
            </div>

            <div className="ml-5 border-l-2 border-gray-300 h-8"></div>

            {/* Dropoff Location */}
            <div className="flex items-start">
              <div className="flex-shrink-0 w-10 h-10 bg-red-500 rounded-full flex items-center justify-center mr-4">
                <MapPin className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-red-700 mb-1">DROP-OFF</p>
                <p className="text-gray-900">{booking?.dropoffLocation?.address}</p>
              </div>
            </div>

            {booking?.totalDistance && (
              <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-between mt-4">
                <span className="text-sm text-gray-600">Total Distance</span>
                <span className="text-lg font-bold text-gray-900">{typeof booking.totalDistance === 'number' ? booking.totalDistance.toFixed(2) : booking.totalDistance || '0.00'} km</span>
              </div>
            )}
          </div>

          {!booking?.otpVerified && (
            <button
              onClick={openPickupInMaps}
              className="w-full mt-6 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center font-medium shadow-sm"
            >
              <Navigation className="h-5 w-5 mr-2" />
              Navigate to Pickup Point
            </button>
          )}

          {/* OTP Display - Compact version below Navigate to Pickup */}
          {booking && !booking.otpVerified && booking.tripOtp && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-300 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <Shield className="h-6 w-6 text-yellow-600 mr-2" />
                  <h3 className="font-bold text-gray-900">Trip Verification Code</h3>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Share this code with your driver to start the trip
                </p>
                <div className="bg-white rounded-lg p-3 text-center border-2 border-yellow-400">
                  <p className="text-3xl font-bold text-gray-900 tracking-widest">
                    {booking.tripOtp}
                  </p>
                </div>
                <div className="mt-3 flex items-start">
                  <AlertCircle className="h-4 w-4 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-gray-600">
                    <strong>Security:</strong> Only share this code with your assigned driver.
                  </p>
                </div>
              </div>
            </div>
          )}

          {booking && !booking.tripOtp && booking.status === 'confirmed' && booking.serviceType === 'driver' && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-3">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 text-yellow-600 mr-2" />
                  <div>
                    <p className="font-semibold text-yellow-900 text-sm">OTP Not Generated</p>
                    <p className="text-xs text-yellow-700">Please contact support.</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Booking Details */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
            <Clock className="h-5 w-5 mr-2 text-black" />
            Booking Details
          </h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Date:</span>
              <span className="font-medium text-gray-900">
                {new Date(booking?.bookingDate || '').toLocaleDateString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Time:</span>
              <span className="font-medium text-gray-900">
                {booking?.tripStartDateTime && booking?.tripEndDateTime
                  ? `${new Date(booking.tripStartDateTime).toLocaleTimeString()} - ${new Date(booking.tripEndDateTime).toLocaleTimeString()}`
                  : 'N/A'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Amount:</span>
              <span className="text-xl font-bold text-green-600">₹{typeof booking?.totalAmount === 'number' ? booking.totalAmount.toFixed(2) : booking?.totalAmount || '0.00'}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerMapView;

